import { useState } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Wallet } from 'lucide-react';

import { useAccounts } from '@/composables/financial/useAccounts';
import { useFinancialReports } from '@/composables/financial/useFinancialReports';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/formatters';
import { rangeForPreset } from '@/lib/dateRange';

const PERIODS = [
  { value: 'all', label: 'All time' },
  { value: 'month', label: 'This month' },
  { value: 'year', label: 'This year' },
];

// All-time needs a dateFrom so the report query enables; far-back start, no upper bound.
function rangeForPeriod(period: string): { dateFrom: string; dateTo?: string } {
  if (period === 'month') return rangeForPreset('this_month');
  if (period === 'year') return rangeForPreset('this_year');
  return { dateFrom: '2000-01-01' };
}

export function FinancialOverviewPage() {
  const { accounts, isLoading } = useAccounts();
  const [period, setPeriod] = useState('all');
  const { pnl, pnlLoading } = useFinancialReports(rangeForPeriod(period));

  const totalBalance = accounts.reduce((sum, a) => sum + Number(a.balance), 0);
  const currency = accounts[0]?.currency || 'INR';
  const totalIncome = Number(pnl?.totalIncome ?? 0);
  const totalExpenses = Number(pnl?.totalExpenses ?? 0);

  return (
    <PageContainer
      title="Financial Overview"
      description="Summary of your financial data"
      actions={
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      }
    >
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Balance</CardTitle><Wallet className="h-4 w-4 text-muted-foreground" /></CardHeader>
          <CardContent>{isLoading ? <Skeleton className="h-8 w-28" /> : <div className="text-2xl font-bold">{formatCurrency(totalBalance, currency)}</div>}</CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Accounts</CardTitle><DollarSign className="h-4 w-4 text-muted-foreground" /></CardHeader>
          <CardContent>{isLoading ? <Skeleton className="h-8 w-12" /> : <div className="text-2xl font-bold">{accounts.length}</div>}</CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Income</CardTitle><TrendingUp className="h-4 w-4 text-green-600" /></CardHeader>
          <CardContent>{pnlLoading ? <Skeleton className="h-8 w-28" /> : <div className="text-2xl font-bold text-green-600">{formatCurrency(totalIncome, currency)}</div>}</CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Expenses</CardTitle><TrendingDown className="h-4 w-4 text-red-600" /></CardHeader>
          <CardContent>{pnlLoading ? <Skeleton className="h-8 w-28" /> : <div className="text-2xl font-bold text-red-600">{formatCurrency(totalExpenses, currency)}</div>}</CardContent>
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-2 mt-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Accounts</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? (
              <>
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </>
            ) : (
              <>
                {accounts.map(a => <div key={a.id} className="flex justify-between items-center p-3 border rounded"><div><p className="font-medium">{a.name}</p><p className="text-xs text-muted-foreground">{a.type}</p></div><span className="font-semibold">{formatCurrency(a.balance, a.currency)}</span></div>)}
                {accounts.length === 0 && <p className="text-muted-foreground text-center py-4">No accounts</p>}
              </>
            )}
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle className="text-base">Quick Actions</CardTitle></CardHeader><CardContent className="space-y-2"><p className="text-sm text-muted-foreground">View reports, create invoices, or record payments from the sidebar navigation.</p></CardContent></Card>
      </div>
    </PageContainer>
  );
}
